import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { Chip, Typography, List } from '@mui/material';
import { StyledListItem } from '../../ShiftInformation.styles';
import { AVAILABILITY_TYPES } from '../../../../utils/constants/availability.constants';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';

// Enable timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const isFullDayType = (eventType) =>
  eventType === AVAILABILITY_TYPES.FULL_DAYS || eventType === AVAILABILITY_TYPES.RECURRING_FULL_DAYS;

function humanRangeTitle(duration, timezone) {
  if (!duration || !timezone) return '';
  const startTime = dayjs(duration[0]).tz(timezone);
  const endTime = dayjs(duration[1]).tz(timezone);
  return `${startTime.format('YYYY-MM-DD HH:mm z')} → ${endTime.format('YYYY-MM-DD HH:mm z')}`;
}

function toDateRange(duration, eventType, formatTime, venueTimezone) {
  if (!duration || !duration[0] || !duration[1]) return 'Unknown time';

  if (eventType === AVAILABILITY_TYPES.FULL_DAYS) {
    const startTime = dayjs(duration[0]).tz(venueTimezone);
    const endTime = dayjs(duration[1]).tz(venueTimezone).subtract(1, 'day');

    const baseText = startTime.isSame(endTime, 'day')
      ? `All day ${startTime.format('MMM DD')}`
      : `All day ${startTime.format('MMM DD')}–${endTime.format('MMM DD')}`;
    const spanDays = endTime.startOf('day').diff(startTime.startOf('day'), 'day') + 1;
    return spanDays > 1 ? `${baseText} • ${spanDays} days` : baseText;
  }

  if (eventType === AVAILABILITY_TYPES.RECURRING_FULL_DAYS) {
    const startTime = dayjs(duration[0]).tz(venueTimezone);
    const endTime = dayjs(duration[1]).tz(venueTimezone).subtract(1, 'day');
    return `Every ${startTime.format('dddd')}, until ${endTime.format('MMM DD')}`;
  }

  if (eventType === 'RECURRING') {
    const startTime = dayjs(duration[0]).tz(venueTimezone);
    return `Every ${startTime.format('dddd')} • ${formatTime(duration[0])} – ${formatTime(duration[1])}`;
  }

  return `${formatTime(duration[0])} – ${formatTime(duration[1])}`;
}

export const ConflictList = ({ conflicts }) => {
  const { formatTime, venueTimezone } = useTimeFormatters();

  return (
    <List>
      {conflicts.map((conflict) => {
        const key = `${conflict.ref || conflict.id || conflict.caption}-${conflict.type}-${conflict.duration?.[0] || ''}`;

        const chipLabel =
          conflict.description || toDateRange(conflict.duration, conflict.type, formatTime, venueTimezone);

        const chipTooltip = humanRangeTitle(conflict.duration, venueTimezone);

        // Handle display for no roles by overriding database captions
        // This is a temporary solution while we modify the database schema
        const employeeHasNoRoles = conflict.caption === 'Employee has no roles';
        const conflictCaption = employeeHasNoRoles ? 'Available roles:' : conflict.caption;
        const chipLabelText = employeeHasNoRoles ? 'None' : chipLabel;

        return (
          <StyledListItem key={key} disableGutters>
            <div>
              <Typography variant="h6" sx={{ m: 0 }}>
                {conflictCaption}
              </Typography>
            </div>
            {chipLabelText ? (
              <Chip
                label={chipLabelText}
                title={chipTooltip}
                variant={isFullDayType(conflict.type) ? 'outlined' : 'filled'}
                size="small"
              />
            ) : null}
          </StyledListItem>
        );
      })}
    </List>
  );
};
